/**
 * Shared API types and utilities
 *
 * DO NOT EDIT THIS FILE MANUALLY!
 * Run 'php artisan routes:generate-types' to regenerate
 */
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { route } from '@/ziggy';

/**
 * HTTP method types
 */
export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

/**
 * Route configuration object containing the route name and method
 * Parameter validation is handled by ESLint plugin 'route-validation/validate-route-params'
 * @template TName - The route name
 * @template TMethod - The HTTP method
 */
export interface RouteConfig<TName extends string = string, TMethod extends HttpMethod = HttpMethod> {
  readonly name: TName;
  readonly method: TMethod;
  readonly params?: unknown;
}

/**
 * Success result type
 */
export interface Success<T> {
  ok: true;
  data: T;
  error?: never;
}

/**
 * Failure result type with typed error
 */
export interface Failure {
  ok: false;
  data?: never;
  error: ApiError;
}

/**
 * Result type for API requests - discriminated union
 * Eliminates the need for try-catch blocks
 *
 * @example
 * const result = await apiRequest<Brand>(API_ROUTES.admin.brands.show, { brand: 1 });
 * if (result.ok) {
 *   console.log(result.data); // Brand
 * } else {
 *   console.error(result.error); // ApiError or subclass
 * }
 */
export type Result<T> = Success<T> | Failure;

/**
 * Base API error class with request context
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public response?: unknown,
    public routeName?: string,
    public method?: string,
    public params?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Validation error (422) with structured field errors
 */
export class ValidationError extends ApiError {
  constructor(
    message: string,
    public errors: Record<string, string[]>,
    response?: unknown,
    routeName?: string,
    method?: string,
    params?: unknown,
  ) {
    super(message, 422, response, routeName, method, params);
    this.name = 'ValidationError';
  }

  /**
   * Get all error messages as a flat array
   */
  getAllMessages(): string[] {
    return Object.values(this.errors).flat();
  }

  /**
   * Get the first error message for a field
   */
  getFieldError(field: string): string | undefined {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return this.errors[field]?.[0];
  }

  /**
   * Check if a specific field has errors
   */
  hasFieldError(field: string): boolean {
    return field in this.errors && this.errors[field].length > 0;
  }
}

/**
 * Unauthorized error (401) - typically means not logged in
 */
export class UnauthorizedError extends ApiError {
  constructor(
    message: string = 'Unauthorized',
    response?: unknown,
    routeName?: string,
    method?: string,
    params?: unknown,
  ) {
    super(message, 401, response, routeName, method, params);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Forbidden error (403) - logged in but lacks permission
 */
export class ForbiddenError extends ApiError {
  constructor(
    message: string = 'Forbidden',
    response?: unknown,
    routeName?: string,
    method?: string,
    params?: unknown,
  ) {
    super(message, 403, response, routeName, method, params);
    this.name = 'ForbiddenError';
  }
}

/**
 * Not found error (404)
 */
export class NotFoundError extends ApiError {
  constructor(
    message: string = 'Not Found',
    response?: unknown,
    routeName?: string,
    method?: string,
    params?: unknown,
  ) {
    super(message, 404, response, routeName, method, params);
    this.name = 'NotFoundError';
  }
}

/**
 * Server error (500+)
 */
export class ServerError extends ApiError {
  constructor(
    message: string = 'Internal Server Error',
    response?: unknown,
    routeName?: string,
    method?: string,
    params?: unknown,
  ) {
    super(message, 500, response, routeName, method, params);
    this.name = 'ServerError';
  }
}

/**
 * Schema validation error - response received but failed Zod parsing
 */
export class SchemaValidationError extends ApiError {
  constructor(
    message: string,
    public receivedData: unknown,
    routeName?: string,
    method?: string,
    params?: unknown,
  ) {
    super(message, 0, receivedData, routeName, method, params);
    this.name = 'SchemaValidationError';
  }
}

/**
 * Route resolution error - invalid route name or parameters
 */
export class RouteError extends ApiError {
  constructor(message: string, routeName?: string, method?: string, params?: unknown) {
    super(message, 0, undefined, routeName, method, params);
    this.name = 'RouteError';
  }
}

/**
 * Generic route helper - can be used with any route object
 */
export function routeHelper<TName extends string>(
  config: RouteConfig<TName, HttpMethod>,
  params?: unknown,
  absolute?: boolean,
): string {
  return route(config.name, params, absolute);
}

/**
 * Generic API request function - can be used with any route configuration
 */
export async function createApiRequest<TResponse = unknown, TConfig extends RouteConfig = RouteConfig>(
  config: TConfig,
  params?: unknown,
  data?: unknown,
  axiosConfig?: AxiosRequestConfig,
): Promise<Result<TResponse>> {
  const method = config.method;

  let url: string;
  try {
    url = route(config.name, params);
  } catch (error) {
    return {
      ok: false,
      error: new RouteError(
        error instanceof Error ? error.message : `Failed to resolve route: ${config.name}`,
        config.name,
        method,
        params,
      ),
    };
  }

  try {
    let response: AxiosResponse<TResponse>;
    switch (method) {
      case 'get': {
        response = await axios.get<TResponse>(url, { ...axiosConfig, params: data });
        break;
      }
      case 'post': {
        response = await axios.post<TResponse>(url, data, axiosConfig);
        break;
      }
      case 'put': {
        response = await axios.put<TResponse>(url, data, axiosConfig);
        break;
      }
      case 'patch': {
        response = await axios.patch<TResponse>(url, data, axiosConfig);
        break;
      }
      case 'delete': {
        response = await axios.delete<TResponse>(url, { ...axiosConfig, params: data });
        break;
      }
      default: {
        throw new Error(`Unsupported HTTP method: ${method}`);
      }
    }

    return { ok: true, data: response.data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const responseData = error.response.data;
      const message = responseData?.message || error.message;

      if (status === 422 && responseData?.errors) {
        return {
          ok: false,
          error: new ValidationError(message, responseData.errors, responseData, config.name, method, params),
        };
      }

      if (status === 401) {
        return {
          ok: false,
          error: new UnauthorizedError(message, responseData, config.name, method, params),
        };
      }

      if (status === 403) {
        return {
          ok: false,
          error: new ForbiddenError(message, responseData, config.name, method, params),
        };
      }

      if (status === 404) {
        return {
          ok: false,
          error: new NotFoundError(message, responseData, config.name, method, params),
        };
      }

      if (status >= 500) {
        return {
          ok: false,
          error: new ServerError(message, responseData, config.name, method, params),
        };
      }

      return {
        ok: false,
        error: new ApiError(message, status, responseData, config.name, method, params),
      };
    }

    return {
      ok: false,
      error: new ApiError(
        error instanceof Error ? error.message : 'An unexpected error occurred',
        0,
        undefined,
        config.name,
        method,
        params,
      ),
    };
  }
}

/**
 * Validates and maps a Result<unknown> to Result<T> using a Zod schema
 * This preserves the Result type while validating the data
 */
function validateResult<T>(
  result: Result<unknown>,
  schema: {
    safeParse: (data: unknown) =>
      | { success: true; data: T }
      | {
          success: false;
          error: { issues: Array<{ path: PropertyKey[]; message: string }>; message: string };
        };
  },
): Result<T> {
  if (!result.ok) {
    return result;
  }

  const parseResult = schema.safeParse(result.data);

  if (!parseResult.success) {
    const issues = parseResult.error.issues;
    const errorDetails =
      issues.length > 0
        ? issues.map(issue => `${issue.path.join('.')}: ${issue.message}`).join(', ')
        : parseResult.error.message || 'Unknown validation error';

    console.error('API Response Validation Failed:', {
      issues: parseResult.error.issues,
      receivedData: result.data,
    });

    return {
      ok: false,
      error: new SchemaValidationError(`API response validation failed: ${errorDetails}`, result.data),
    };
  }

  return { ok: true, data: parseResult.data };
}

/**
 * Makes an API request with automatic Zod validation
 *
 * TypeScript will enforce that you pass the correct parameters based on the route config.
 *
 * @example
 * // Routes with params
 * const result = await validatedApiRequest(
 *   API_ROUTES.admin.brands.show,
 *   BrandResponseSchema,
 *   { brandId: '123' }  // Must use exact parameter names
 * );
 *
 * // Routes without params
 * const result = await validatedApiRequest(
 *   API_ROUTES.admin.dashboard,
 *   DashboardSchema
 * );
 */
export async function validatedApiRequest<T, TConfig extends RouteConfig = RouteConfig>(
  config: TConfig,
  schema: {
    safeParse: (data: unknown) =>
      | { success: true; data: T }
      | {
          success: false;
          error: { issues: Array<{ path: PropertyKey[]; message: string }>; message: string };
        };
  },
  params?: unknown,
  data?: unknown,
  axiosConfig?: AxiosRequestConfig,
): Promise<Result<T>> {
  const result = await createApiRequest(config, params, data, axiosConfig);
  return validateResult(result, schema);
}
